import styled from 'styled-components';
import { PageContainer } from '../../../components/PageContainer';
import { SubscriptionCard } from '../components/SubscriptionCard';
import { PaymentDetails } from '../components/PaymentDetails';
import {
  useLazyCreatePortalSessionQuery,
  useGetPaymentMethodsQuery,
  useGetSubscriptionPlansQuery,
  useCheckPauseStatusQuery
} from '../api/subscriptionApi';
import { FullScreenSpinner } from '../../../components/Spinner/Spinner';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { useState } from 'react';

import { usePauseSubscriptionModal } from '../hooks/usePauseSubscriptionModal';
import { useSwitchPlanModal } from '../hooks/useSwitchPlanModal';

export const Subscription = () => {
  const { stripeId } = useParams<{ stripeId: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate(); // Add navigate hook

  const { data: subscriptionsData, isLoading } = useGetSubscriptionPlansQuery({
    customerId: stripeId || ''
  });

  const { PauseSubscriptionModal } = usePauseSubscriptionModal({
    stripeId: stripeId || '',
    isOpen,
    setIsOpen,
    onPauseSuccess: () => {
      // Navigate back to dashboard after a successful pause with a refresh parameter
      navigate('/dashboard?refresh=true');
    }
  });
  const { ConfirmationModal, onButtonPress } = useSwitchPlanModal({
    customerId: stripeId || ''
  });
  const [createPortalSession, { isLoading: loadingPortalSession }] =
    useLazyCreatePortalSessionQuery();

  const { data: pauseStatus } = useCheckPauseStatusQuery({ customerId: stripeId || '' });
  const hasPauseReminder = pauseStatus?.active;

  const subscribedPlan = subscriptionsData?.eligible_plans?.find((plan) => plan.subscribed);
  const isMonthlyPlan = subscribedPlan?.recurring?.interval === 'month';

  // Order the plan grid by tier (cheapest first), with each tier's monthly
  // price on the left and its annual price beside it — instead of raw Stripe
  // list order.
  const eligiblePlans = subscriptionsData?.eligible_plans ?? [];
  const monthlyEquivalent = (plan: (typeof eligiblePlans)[number]) =>
    plan.recurring?.interval === 'year' ? plan.unit_amount / 12 : plan.unit_amount;
  const tierRank: Record<string, number> = {};
  eligiblePlans.forEach((plan) => {
    tierRank[plan.product_name] = Math.min(
      tierRank[plan.product_name] ?? Infinity,
      monthlyEquivalent(plan)
    );
  });
  const sortedPlans = [...eligiblePlans].sort(
    (a, b) =>
      tierRank[a.product_name] - tierRank[b.product_name] ||
      (a.recurring?.interval === 'year' ? 1 : 0) - (b.recurring?.interval === 'year' ? 1 : 0) ||
      a.unit_amount - b.unit_amount
  );

  const { data: paymentMethods } = useGetPaymentMethodsQuery({ customerId: stripeId || '' });

  const onPortalSession = async () => {
    try {
      const { data } = await createPortalSession({ customerId: stripeId || '' });
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Check if this is a multi-SIM account
  const isMultiSimAccount = subscriptionsData?.is_multi_sim_account;

  return (
    <PageContainer title="Change subscription" onGoBack="/dashboard">
      {isLoading ? (
        <FullScreenSpinner />
      ) : (
        <Wrapper>
          {isMultiSimAccount ? (
            // Multi-SIM account view - simplified, no plan switching
            <>
              <MultiSimBanner>
                <BannerIcon>📦</BannerIcon>
                <BannerContent>
                  <BannerTitle>Multi-SIM Account</BannerTitle>
                  <BannerDescription>
                    You have {subscriptionsData?.sim_count} SIMs on this account. 
                    To make changes to your subscription or add additional SIMs, please contact our support team.
                  </BannerDescription>
                </BannerContent>
              </MultiSimBanner>

              {subscriptionsData?.current_plan && (
                <PlansSection>
                  <SectionTitle>Your Current Plan</SectionTitle>
                  <CurrentPlanCard>
                    <PlanName>{subscriptionsData.current_plan.name}</PlanName>
                    <PlanPrice>
                      {new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: subscriptionsData.current_plan.currency || 'gbp'
                      }).format((subscriptionsData.current_plan.unit_amount || 0) / 100)}
                      <span>/{subscriptionsData.current_plan.interval}</span>
                    </PlanPrice>
                    <PlanQuantity>× {subscriptionsData.sim_count} SIMs</PlanQuantity>
                    {subscriptionsData.current_plan.total_amount !== undefined && <p>
                      Total: {new Intl.NumberFormat('en-GB', { style: 'currency', currency: subscriptionsData.current_plan.currency }).format(subscriptionsData.current_plan.total_amount / 100)} / {subscriptionsData.current_plan.interval}
                    </p>}
                    {subscriptionsData.current_plan.renewal_at && <p>
                      Renews {new Date(subscriptionsData.current_plan.renewal_at * 1000).toLocaleDateString('en-GB')}
                    </p>}
                  </CurrentPlanCard>
                </PlansSection>
              )}

              <TopActionsWrapper>
                <ActionCard>
                  <ActionHeader>
                    <ActionIcon>💳</ActionIcon>
                    <ActionTitle>Update Payment Method</ActionTitle>
                  </ActionHeader>
                  <ActionDescription>Update your card details or manage billing</ActionDescription>
                  {paymentMethods?.payment_methods && paymentMethods.payment_methods.length > 0 && (
                    <CardsWrapper>
                      {paymentMethods.payment_methods.map((paymentMethod) => (
                        <PaymentDetails paymentDetail={paymentMethod} key={paymentMethod.last4} />
                      ))}
                    </CardsWrapper>
                  )}
                  <ActionFooter>
                    <Button
                      onClick={onPortalSession}
                      isLoading={loadingPortalSession}
                      style={{ width: '100%' }}>
                      Update payment details
                    </Button>
                  </ActionFooter>
                </ActionCard>
                <ActionCard>
                  <ActionHeader>
                    <ActionIcon>💬</ActionIcon>
                    <ActionTitle>Need Help?</ActionTitle>
                  </ActionHeader>
                  <ActionDescription>
                    Contact us to modify your subscription or add SIMs
                  </ActionDescription>
                  <ActionFooter>
                    <SupportEmail>support@farmstream.co.uk</SupportEmail>
                  </ActionFooter>
                </ActionCard>
              </TopActionsWrapper>
            </>
          ) : (
            // Standard single-SIM account view
            <>
              <AccountActionsBar>
                <ActionRow>
                  <ActionRowLeft>
                    <ActionIcon>💳</ActionIcon>
                    <ActionRowText>
                      <ActionTitle>Payment Method</ActionTitle>
                      <ActionDescription>Update your card details or manage billing</ActionDescription>
                    </ActionRowText>
                  </ActionRowLeft>
                  <ActionRowRight>
                    {paymentMethods?.payment_methods && paymentMethods.payment_methods.length > 0 && (
                      <PaymentDetails
                        paymentDetail={paymentMethods.payment_methods[0]}
                        key={paymentMethods.payment_methods[0].last4}
                      />
                    )}
                    <Button onClick={onPortalSession} isLoading={loadingPortalSession}>
                      Update
                    </Button>
                  </ActionRowRight>
                </ActionRow>

                <RowDivider />

                <ActionRow>
                  <ActionRowLeft>
                    <ActionIcon variant="danger">⏸️</ActionIcon>
                    <ActionRowText>
                      <ActionTitle>Pause Subscription</ActionTitle>
                      <ActionDescription>
                        {isMonthlyPlan
                          ? 'Temporarily pause your subscription'
                          : 'Pausing is only available on monthly plans'}
                      </ActionDescription>
                    </ActionRowText>
                  </ActionRowLeft>
                  <ActionRowRight>
                    <Button
                      variant="danger"
                      onClick={() => setIsOpen(true)}
                      disabled={!isMonthlyPlan}>
                      Pause
                    </Button>
                  </ActionRowRight>
                </ActionRow>
              </AccountActionsBar>

              <PlansSection>
                {subscriptionsData?.on_legacy_price && subscribedPlan && (
                  <LegacyPriceCard>
                    <LegacyBadge>CURRENT PLAN</LegacyBadge>
                    <LegacyPlanInfo>
                      <LegacyPlanName>{subscribedPlan.product_name}</LegacyPlanName>
                      <LegacyPlanPrice>
                        {new Intl.NumberFormat('en-GB', {
                          style: 'currency',
                          currency: subscribedPlan.currency || 'gbp'
                        }).format((subscribedPlan.unit_amount || 0) / 100)}
                        <span>/{subscribedPlan.recurring?.interval}</span>
                      </LegacyPlanPrice>
                    </LegacyPlanInfo>
                    <LegacyMessage>
                      We've recently updated our pricing tiers. As a valued customer, you've been kept on a <strong>higher data plan at a lower price</strong> — no action needed. Please note, if you switch to another plan this price will no longer be available.
                    </LegacyMessage>
                  </LegacyPriceCard>
                )}

                <SectionTitle>Available Plans</SectionTitle>
                <Grid>
                  {sortedPlans
                    .filter((plan) => !(subscriptionsData?.on_legacy_price && plan.subscribed))
                    .map((subscription) => (
                      <SubscriptionCard
                        key={subscription.id}
                        subscription={subscription}
                        isCurrentPlan={subscription.subscribed}
                        onButtonPress={onButtonPress}
                      />
                    ))}
                </Grid>
              </PlansSection>
            </>
          )}
        </Wrapper>
      )}
      {PauseSubscriptionModal}
      {ConfirmationModal}
    </PageContainer>
  );
};


const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding-top: 0.5rem;
  padding-bottom: 2rem;

  @media (max-width: 850px) {
    gap: 1.25rem;
  }
`;

const MultiSimBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}10, ${({ theme }) => theme.colors.primary}05);
  border: 2px solid ${({ theme }) => theme.colors.primary}40;
  border-radius: 1rem;
  padding: 1.5rem;

  @media (max-width: 650px) {
    padding: 1rem;
    gap: 0.75rem;
  }
`;

const BannerIcon = styled.div`
  font-size: 2rem;
  flex-shrink: 0;
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BannerTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const BannerDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.small};
  color: ${({ theme }) => theme.colors.gray500};
  line-height: 1.5;
`;

const CurrentPlanCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.white}, ${({ theme }) => theme.colors.gray}15);
  border: 2px solid ${({ theme }) => theme.colors.green}40;
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;

  @media (max-width: 650px) {
    padding: 1.5rem 1rem;
  }
`;

const PlanName = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  
  span {
    font-size: 1rem;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.gray500};
  }
`;

const PlanQuantity = styled.div`
  font-size: 1.125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.green};
  background: ${({ theme }) => theme.colors.green}15;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  margin-top: 0.5rem;
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 0.5rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary}dd;
    transform: translateY(-1px);
  }
`;

const CardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
`;

const TopActionsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  align-items: stretch;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const AccountActionsBar = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray500}25;
  border-radius: 1rem;
  padding: 0.5rem 1.25rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  @media (max-width: 650px) {
    padding: 0.5rem 1rem;
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 0;

  @media (max-width: 650px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.85rem 0;
  }
`;

const ActionRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
`;

const ActionRowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;

  @media (max-width: 650px) {
    width: 100%;
    flex-wrap: wrap;

    button {
      flex: 1;
    }
  }
`;

const ActionRowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
`;

const RowDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.gray500}20;
`;

const ActionCard = styled.div<{ variant?: 'danger' }>`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.white}, ${({ theme }) => theme.colors.gray}15);
  border: 1px solid ${({ theme, variant }) => variant === 'danger' ? theme.colors.red + '30' : theme.colors.primary + '30'};
  border-radius: 0.75rem;
  padding: 1.1rem 1.25rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
    border-color: ${({ theme, variant }) => variant === 'danger' ? theme.colors.red : theme.colors.primary};
  }
`;

const ActionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const ActionIcon = styled.div<{ variant?: 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.6rem;
  background: ${({ theme, variant }) => variant === 'danger' ? theme.colors.red + '12' : theme.colors.primary + '12'};
  font-size: 1.05rem;
  flex-shrink: 0;
`;

const ActionFooter = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 0.25rem;
`;

const ActionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const ActionDescription = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  color: ${({ theme }) => theme.colors.gray500};
  line-height: 1.4;
`;

const SupportEmail = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}10;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  text-align: center;
  user-select: all;
  cursor: text;
`;

const PlansSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.white}, ${({ theme }) => theme.colors.gray}15);
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);

  @media (max-width: 850px) {
    padding: 1rem;
    gap: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.35rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
  text-align: center;
  letter-spacing: -0.01em;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 0.5rem;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const LegacyPriceCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  background: #e8f5e9;
  border: 2px solid #a5d6a7;
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
  position: relative;
  margin-bottom: 1rem;

  @media (max-width: 650px) {
    padding: 1.25rem 1rem;
    margin-bottom: 0.5rem;
  }
`;

const LegacyBadge = styled.span`
  position: absolute;
  top: -0.6rem;
  right: 1rem;
  background: ${({ theme }) => theme.colors.green900 || '#0F9D58'};
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.25rem 0.6rem;
  border-radius: 0.25rem;
  letter-spacing: 0.05em;
`;

const LegacyPlanInfo = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;
`;

const LegacyPlanName = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

const LegacyPlanPrice = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.green900 || '#0F9D58'};

  span {
    font-size: 0.8rem;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.gray500};
  }
`;

const LegacyMessage = styled.p`
  margin: 0.25rem 0 0;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  color: #2e7d32;
  text-align: center;
  line-height: 1.4;
`;
